import React, { useEffect, useRef } from "react";
import { useVisibilityChange } from "@uidotdev/usehooks";
import { Navigate, Route, Routes } from "react-router-dom";

import { discountActions, productsActions, productsSettingsActions } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope, RequestStatus } from "@fitness-app/data-models";

import { usePermission } from "~/hooks/usePermission";
import { PermissionGuard } from "~/modules/Auth/PermissionGuard";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import CollectionsCasePage from "~/modules/Collections/CollectionsCasePage";
import Product from "~/modules/Products/Product/Product";
import ProductRedirect from "~/modules/Products/Product/ProductRedirect";
import ProductResourcesCatalog from "~/modules/Products/ProductResourcesCatalog";
import Products from "~/modules/Products/Products";
import CustomerDetails from "~/modules/Products/ProductsCustomers/CustomerDetails";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

const ProductsRouter = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const documentVisible = useVisibilityChange();
  const afterInit = useRef(false);
  const productsListStatus = useAppSelector((store) => store.products.productsListStatus);
  const canManageProducts = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);

  useEffect(() => {
    if (canManageProducts && documentVisible && productsListStatus !== RequestStatus.SUBSCRIBED && afterInit.current) {
      dispatch(productsActions.subscribeToProducts());
      dispatch(productsSettingsActions.subscribeToProductsSettings());
      dispatch(discountActions.subscribeToDiscounts());
    }
  }, [canManageProducts, dispatch, documentVisible, productsListStatus]);

  useEffect(() => {
    if (canManageProducts) {
      dispatch(productsActions.subscribeToProducts());
      dispatch(productsSettingsActions.subscribeToProductsSettings());
      dispatch(discountActions.subscribeToDiscounts());
    }

    afterInit.current = true;

    return () => {
      if (canManageProducts) {
        dispatch(productsActions.unsubscribeFromProducts());
        dispatch(productsSettingsActions.unsubscribeFromProductsSettings());
        dispatch(discountActions.unsubscribeFromDiscounts());
      }
    };
  }, [canManageProducts, dispatch]);

  return (
    <Routes>
      <Route index element={canManageProducts ? <Products /> : <ProductResourcesCatalog />} />
      <Route
        path="/customers/:customerId"
        element={
          <PermissionGuard requirement={dashboardPermissionRequirements.productsCustomers}>
            <CustomerDetails />
          </PermissionGuard>
        }
      />
      {/* Należności (TRE-2168): karta sprawy żyje pod Produktami; trasa musi stać przed `/:id`. */}
      <Route path="/collections" element={<Navigate to="/products?tab=collections" replace />} />
      <Route
        path="/collections/:caseId"
        element={
          <PermissionGuard requirement={dashboardPermissionRequirements.productsManage}>
            <CollectionsCasePage />
          </PermissionGuard>
        }
      />
      <Route path="/:id" element={<ProductRedirect />} />
      <Route path="/:id/:tab/*" element={<Product />} />
    </Routes>
  );
};

export default ProductsRouter;
